import React from 'react';
import { ComponentType } from 'dto/raptor-component.dto';
import { ManifestPlatformDto } from 'dto/raptor-component-version.dto';
import { BasicCollapse, FormInstance, KeyValueDisplay, Typography } from 'ui-lib';
import { ComponentVersionDisplayHelpers } from '../component-version.helpers';
import {
    IComponentVersionWizardComponent,
    IComponentVersionFormValues,
    IComponentVersionManifestValues,
} from '../component-version-wizard.types';
import { SupportedPlatformsSummary } from '../supported-platforms-summary';
import css from '../../raptor-components.css';

interface IReviewStepProps {
    component: IComponentVersionWizardComponent;
    selectedPlatforms: ManifestPlatformDto[];
    form: FormInstance<IComponentVersionFormValues>;
}

interface ISpecificReviewProps {
    values: IComponentVersionFormValues;
    selectedPlatforms: ManifestPlatformDto[];
}

const renderManifestReviewPanels = (
    values: IComponentVersionFormValues,
    selectedPlatforms: ManifestPlatformDto[],
    renderManifestContent: (manifest: IComponentVersionManifestValues) => React.ReactNode,
): JSX.Element => {
    return (
        <BasicCollapse
            key={selectedPlatforms.map((platform) => platform.uuid).join('|')}
            className={css['manifest-panel']}
            defaultActiveKey={selectedPlatforms.map((platform) => platform.uuid)}
            panels={selectedPlatforms.map((platform) => ({
                key: platform.uuid,
                header: platform.displayName,
                content: renderManifestContent(values.manifests?.[platform.uuid] || {}),
            }))}
        />
    );
};

const formatConfigurationJson = (configurationJson?: string): string => {
    if (!configurationJson?.trim()) {
        return '{}';
    }
    try {
        return JSON.stringify(JSON.parse(configurationJson), null, 2);
    } catch {
        return configurationJson;
    }
};

const CatapultReview = (props: ISpecificReviewProps): JSX.Element => {
    const { values, selectedPlatforms } = props;
    const renderPreformatted = (value: unknown): JSX.Element => (
        <pre className={css['details-preformatted']}>{ComponentVersionDisplayHelpers.formatDisplayValue(value)}</pre>
    );

    return (
        <React.Fragment>
            <Typography.Title level={5}>Catapult configuration</Typography.Title>
            <pre className={css['review-payload']}>{formatConfigurationJson(values.configurationJson)}</pre>
            <Typography.Title level={5}>Lifecycle scripts</Typography.Title>
            {renderManifestReviewPanels(values, selectedPlatforms, (manifest) => {
                return (
                    <KeyValueDisplay
                        column={1}
                        layout='vertical'
                        items={[
                            {
                                title: 'Install',
                                content: renderPreformatted(manifest?.lifecycle?.install),
                            },
                            {
                                title: 'Run',
                                content: renderPreformatted(manifest?.lifecycle?.run),
                            },
                            {
                                title: 'Startup',
                                content: renderPreformatted(manifest?.lifecycle?.startup),
                            },
                            {
                                title: 'Artifacts',
                                content: ComponentVersionDisplayHelpers.renderRecords(manifest.uploadedFiles, 'fileName'),
                            },
                        ]}
                    />
                );
            })}
        </React.Fragment>
    );
};

const DockerFileReview = (props: ISpecificReviewProps): JSX.Element => {
    const { values, selectedPlatforms } = props;

    return (
        <React.Fragment>
            <Typography.Title level={5}>Manifests</Typography.Title>
            {renderManifestReviewPanels(values, selectedPlatforms, (manifest) => {
                return (
                    <KeyValueDisplay
                        column={1}
                        items={[
                            {
                                title: 'Artifact',
                                content: ComponentVersionDisplayHelpers.renderRecords(manifest.uploadedFiles, 'fileName'),
                            },
                            {
                                title: 'Docker Image',
                                content: ComponentVersionDisplayHelpers.renderRecords(manifest.dockerImages, 'displayName'),
                            },
                        ]}
                    />
                );
            })}
        </React.Fragment>
    );
};

const DockerImageReview = (props: ISpecificReviewProps): JSX.Element => {
    const { values, selectedPlatforms } = props;

    return (
        <React.Fragment>
            <Typography.Title level={5}>Manifests</Typography.Title>
            {renderManifestReviewPanels(values, selectedPlatforms, (manifest) => {
                return (
                    <KeyValueDisplay
                        column={1}
                        items={[
                            {
                                title: 'Docker Image',
                                content: ComponentVersionDisplayHelpers.renderRecords(manifest.dockerImages, 'displayName'),
                            },
                        ]}
                    />
                );
            })}
        </React.Fragment>
    );
};

const DockerComposeReview = (props: ISpecificReviewProps): JSX.Element => {
    const { values, selectedPlatforms } = props;

    return (
        <React.Fragment>
            <Typography.Title level={5}>Manifests</Typography.Title>
            {renderManifestReviewPanels(values, selectedPlatforms, (manifest) => {
                return (
                    <KeyValueDisplay
                        column={1}
                        items={[
                            {
                                title: 'Compose File',
                                content: ComponentVersionDisplayHelpers.renderRecords(manifest.uploadedFiles, 'fileName'),
                            },
                            {
                                title: 'Docker Images',
                                content: ComponentVersionDisplayHelpers.renderRecords(manifest.dockerImages, 'displayName'),
                            },
                        ]}
                    />
                );
            })}
        </React.Fragment>
    );
};

const renderSpecificReview = {
    [ComponentType.CATAPULT]: CatapultReview,
    [ComponentType.DOCKER_FILE]: DockerFileReview,
    [ComponentType.DOCKER_IMAGE]: DockerImageReview,
    [ComponentType.DOCKER_COMPOSE]: DockerComposeReview,
};

export const ReviewStep = (props: IReviewStepProps): JSX.Element => {
    const values = props.form.getFieldsValue(true) as IComponentVersionFormValues;
    const supportedPlatformItems = ComponentVersionDisplayHelpers.getSupportedPlatformItems(props.selectedPlatforms);

    const SpecificReview = renderSpecificReview[props.component.type as ComponentType] || DockerImageReview;

    return (
        <React.Fragment>
            <div className={css['review-summary']}>
                <KeyValueDisplay
                    column={1}
                    items={[
                        {
                            title: 'Component',
                            content: ComponentVersionDisplayHelpers.formatDisplayValue(props.component.displayName),
                        },
                        {
                            title: 'Type',
                            content: ComponentVersionDisplayHelpers.formatDisplayValue(props.component.type),
                        },
                        {
                            title: 'Version',
                            content: ComponentVersionDisplayHelpers.formatDisplayValue(values.version?.trim()),
                        },
                        {
                            title: 'Description',
                            content: ComponentVersionDisplayHelpers.formatDisplayValue(values.description?.trim()),
                        },
                    ]}
                />
            </div>
            <SupportedPlatformsSummary items={supportedPlatformItems} />
            <SpecificReview values={values} selectedPlatforms={props.selectedPlatforms} />
        </React.Fragment>
    );
};
